<template>
  <v-container fluid class="intelligent-picking">
    <!-- Header con estadísticas -->
    <v-row class="mb-4">
      <v-col cols="12">
        <div class="d-flex justify-space-between align-center mb-4 picking-header">
          <h1 class="text-h4 font-weight-bold">Picking Inteligente</h1>
          <v-btn 
            color="primary" 
            @click="showCreateTaskDialog = true" 
            prepend-icon="mdi-plus"
            class="picking-create-btn"
          >
            <span class="d-none d-sm-inline">Nueva Tarea</span>
            <span class="d-inline d-sm-none">Nueva</span>
          </v-btn>
        </div>
      </v-col>
    </v-row>

    <!-- Estadísticas del Dashboard -->
    <v-row class="mb-6">
      <v-col cols="12" sm="6" md="3">
        <v-card class="stats-card">
          <v-card-text>
            <div class="d-flex align-center">
              <v-icon color="primary" size="40" class="mr-3">mdi-clipboard-list</v-icon>
              <div>
                <div class="text-h4 font-weight-bold">{{ stats.activeTasks }}</div>
                <div class="text-subtitle-2 text-grey">Tareas Activas</div>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <v-card class="stats-card">
          <v-card-text>
            <div class="d-flex align-center">
              <v-icon color="warning" size="40" class="mr-3">mdi-timer</v-icon>
              <div>
                <div class="text-h4 font-weight-bold">{{ stats.avgTime }}min</div>
                <div class="text-subtitle-2 text-grey">Tiempo Promedio</div>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <v-card class="stats-card">
          <v-card-text>
            <div class="d-flex align-center">
              <v-icon color="info" size="40" class="mr-3">mdi-chart-line</v-icon>
              <div>
                <div class="text-h4 font-weight-bold">{{ stats.efficiency }}%</div>
                <div class="text-subtitle-2 text-grey">Eficiencia</div>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <v-card class="stats-card">
          <v-card-text>
            <div class="d-flex align-center">
              <v-icon color="success" size="40" class="mr-3">mdi-package-variant</v-icon>
              <div>
                <div class="text-h4 font-weight-bold">{{ stats.itemsPicked }}</div>
                <div class="text-subtitle-2 text-grey">Items Procesados</div>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Filtros -->
    <v-card class="mb-4 picking-filters">
      <v-card-text>
        <v-row>
          <v-col cols="12" sm="6" md="3">
            <v-select
              v-model="filters.status"
              :items="statusOptions"
              item-title="text"
              item-value="value"
              label="Estado"
              clearable
              @update:model-value="loadTasks"
              density="compact"
            ></v-select>
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-select
              v-model="filters.strategy"
              :items="strategyOptions"
              item-title="text"
              item-value="value"
              label="Estrategia"
              clearable
              @update:model-value="loadTasks"
              density="compact"
            ></v-select>
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-text-field
              v-model="filters.date_from"
              type="date"
              label="Desde"
              @update:model-value="loadTasks"
              density="compact"
            ></v-text-field>
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-text-field
              v-model="filters.date_to"
              type="date"
              label="Hasta"
              @update:model-value="loadTasks"
              density="compact"
            ></v-text-field>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Tabla de Tareas -->
    <v-card>
      <v-card-title>
        <span class="text-h6">Tareas de Picking</span>
        <v-spacer></v-spacer>
        <v-btn icon @click="loadTasks">
          <v-icon>mdi-refresh</v-icon>
        </v-btn>
      </v-card-title>
      
      <ResponsiveDataTable
        :headers="taskHeaders"
        :items="tasks"
        :loading="loading"
        class="tasks-table"
        item-key="id"
      >
        <template v-slot:item.task_number="{ item }">
          <v-btn variant="text" color="primary" @click="viewTask(item)">
            {{ item.task_number }}
          </v-btn>
        </template>

        <template v-slot:item.status="{ item }">
          <v-chip :color="getStatusColor(item.status)" size="small">
            {{ getStatusText(item.status) }}
          </v-chip>
        </template>

        <template v-slot:item.priority="{ item }">
          <v-rating
            :model-value="item.priority"
            readonly
            size="small"
            density="compact"
            color="primary"
          ></v-rating>
        </template>

        <template v-slot:item.piece_by_piece_enabled="{ item }">
          <v-chip 
            :color="item.piece_by_piece_enabled ? 'primary' : 'grey'" 
            size="small"
            :prepend-icon="item.piece_by_piece_enabled ? 'mdi-barcode-scan' : 'mdi-package-variant'"
          >
            {{ item.piece_by_piece_enabled ? 'Habilitado' : 'Deshabilitado' }}
          </v-chip>
        </template>

        <template v-slot:item.estimated_time="{ item }">
          {{ item.estimated_time || 0 }} min
        </template>

        <template v-slot:item.created_at="{ item }">
          {{ formatDate(item.created_at || item.order_date) }}
        </template>

        <template v-slot:item.actions="{ item }">
          <v-menu>
            <template v-slot:activator="{ props }">
              <v-btn icon="mdi-dots-vertical" v-bind="props" size="small"></v-btn>
            </template>
            <v-list>
              <v-list-item @click="viewTask(item)">
                <v-list-item-title>Ver Detalles</v-list-item-title>
              </v-list-item>
              <v-list-item @click="editTask(item)" v-if="item.status === 'pending'">
                <v-list-item-title>Editar</v-list-item-title>
              </v-list-item>
              <v-list-item @click="allocateTask(item)" v-if="item.status === 'pending'">
                <v-list-item-title>Asignar Inventario</v-list-item-title>
              </v-list-item>
              <v-list-item @click="createPickingList(item)" v-if="item.status === 'assigned'">
                <v-list-item-title>Crear Lista Picking</v-list-item-title>
              </v-list-item>
              <v-list-item 
                @click="startTask(item)" 
                v-if="item.status === 'assigned' || item.status === 'pending'"
                style="color: green;"
              >
                <template v-slot:prepend>
                  <v-icon>mdi-play</v-icon>
                </template>
                <v-list-item-title>🚀 Ejecutar Picking</v-list-item-title>
              </v-list-item>
              <v-list-item @click="createShipment(item)" v-if="item.status === 'picked'">
                <v-list-item-title>Crear Envío</v-list-item-title>
              </v-list-item>
              <v-list-item @click="releaseAllocations(item)" v-if="item.status === 'assigned'">
                <v-list-item-title>Liberar Asignaciones</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </template>
      </ResponsiveDataTable>
    </v-card>

    <!-- Dialog para crear/editar tarea -->
    <v-dialog v-model="showCreateTaskDialog" max-width="1200px" persistent>
      <v-card>
        <v-card-title>
          <span class="text-h5">{{ editingTask ? 'Editar Tarea' : 'Nueva Tarea de Picking' }}</span>
        </v-card-title>
        
        <v-card-text>
          <v-form ref="taskForm">
            <v-row>
              <!-- Campo Tipo de Picking comentado - sin utilidad
              <v-col cols="12" md="6">
                <v-select
                  v-model="taskForm.picking_type"
                  :items="pickingTypeOptions"
                  item-title="text"
                  item-value="value"
                  label="Tipo de Picking *"
                  :rules="[v => !!v || 'Tipo de picking es requerido']"
                  required
                ></v-select>
              </v-col>
              -->
              <v-col cols="12">
                <PickingStrategySelector
                  v-model="pickingStrategy"
                  :strategies="pickingStrategyOptions"
                  @strategy-changed="onPickingStrategyChanged"
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-rating
                  v-model="taskForm.priority"
                  label="Prioridad"
                  max="5"
                  color="primary"
                ></v-rating>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="taskForm.required_date"
                  type="datetime-local"
                  label="Fecha Requerida"
                ></v-text-field>
              </v-col>
              
              <!-- Switch de Picking Piece-by-Piece -->
              <v-col cols="12">
                <v-card variant="outlined" class="pa-3">
                  <div class="d-flex align-center">
                    <v-switch
                      v-model="taskForm.piece_by_piece_enabled"
                      color="primary"
                      class="mr-3"
                    ></v-switch>
                    <div class="flex-grow-1">
                      <div class="text-subtitle-1 font-weight-medium">
                        Picking Piece-by-Piece con Validación
                      </div>
                      <div class="text-caption text-grey">
                        Requiere escaneo obligatorio de códigos de barras antes de completar el picking de cada item
                      </div>
                    </div>
                    <v-icon 
                      :color="taskForm.piece_by_piece_enabled ? 'primary' : 'grey'" 
                      size="24"
                    >
                      mdi-barcode-scan
                    </v-icon>
                  </div>
                </v-card>
              </v-col>
              
              <v-col cols="12">
                <v-textarea
                  v-model="taskForm.notes"
                  label="Notas adicionales"
                  rows="2"
                ></v-textarea>
              </v-col>
            </v-row>

            <!-- Líneas de Tarea -->
            <v-divider class="my-4"></v-divider>
            <div class="d-flex justify-space-between align-center mb-4">
              <h3>Productos a Recolectar</h3>
              <v-btn color="primary" @click="addTaskLine" size="small">
                <v-icon>mdi-plus</v-icon> Agregar Producto
              </v-btn>
            </div>

            <v-card v-for="(line, index) in taskForm.task_lines" :key="index" class="mb-3">
              <v-card-text>
                <v-row>
                  <v-col cols="12" md="5">
                    <v-autocomplete
                      v-model="line.product_id"
                      :items="getFilteredProductsForTaskLine(index)"
                      item-title="name"
                      item-value="id"
                      label="Producto *"
                      :rules="[v => !!v || 'Producto es requerido']"
                      @update:model-value="updateLineProduct(line, $event)"
                      clearable
                      :search="line.productSearch"
                      @update:search="(search) => handleProductSearchForTaskLine(search, index)"
                      :menu-props="{ maxHeight: 300, closeOnContentClick: false }"
                      :no-data-text="line.productSearch && line.productSearch.length >= 2 ? 'No se encontraron productos' : 'Escribe SKU o nombre para buscar...'"
                      :custom-filter="() => true"
                      auto-select-first
                      hide-no-data
                    >
                      <template v-slot:item="{ props, item }">
                        <v-list-item 
                          v-bind="props" 
                          :disabled="!item.raw.has_stock"
                          :class="{ 'text-grey': !item.raw.has_stock }"
                        >
                          <v-list-item-title>
                            {{ item.raw.name }}
                            <v-chip 
                              v-if="!item.raw.has_stock" 
                              size="x-small" 
                              color="error" 
                              class="ml-2"
                            >
                              Sin Stock
                            </v-chip>
                          </v-list-item-title>
                          <v-list-item-subtitle>
                            SKU: {{ item.raw.sku }} | 
                            Stock: {{ item.raw.available_quantity }} {{ item.raw.unit_of_measure }}
                          </v-list-item-subtitle>
                        </v-list-item>
                      </template>
                    </v-autocomplete>
                  </v-col>
                  <v-col cols="12" md="3">
                    <v-text-field
                      v-model.number="line.quantity"
                      type="number"
                      label="Cantidad *"
                      :rules="[
                        v => v > 0 || 'Cantidad debe ser mayor a 0',
                        v => !line.max_quantity || v <= line.max_quantity || `Máximo disponible: ${line.max_quantity}`
                      ]"
                      :max="line.max_quantity"
                      :hint="getQuantityHint(line)"
                      persistent-hint
                      @update:model-value="onQuantityChanged(line, $event)"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" md="2">
                    <v-text-field
                      :model-value="line.unit_of_measure"
                      label="Unidad"
                      readonly
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" md="2">
                    <v-btn
                      color="error"
                      icon="mdi-delete"
                      @click="removeTaskLine(index)"
                      size="small"
                    ></v-btn>
                  </v-col>
                </v-row>
                
                <!-- Estrategia de picking por producto y criterios granulares -->
                <PickingCriteriaSelector
                  v-if="line.product_id"
                  v-model:picking-strategy="line.picking_strategy"
                  v-model:picking-criteria="line.picking_criteria"
                  :picking-strategy-config="pickingStrategy"
                  :available-lots="line.available_lots"
                  :available-serials="line.available_serials"
                  :available-expiry-dates="line.available_expiry_dates" 
                  :has-any-criteria="line.has_any_criteria"
                  :can-select-criteria="line.can_select_criteria"
                  :criteria-error="line.criteria_error"
                  :strategies="pickingStrategyOptions"
                  :show-criteria-selectors="showCriteriaSelectors(line, index)"
                  @criteria-changed="onCriteriaChanged(line, index, $event)"
                  @strategy-changed="onLineStrategyChanged(line, index, $event)"
                />

                <!-- Mostrar criterios seleccionados automáticamente o manualmente -->
                <v-card 
                  v-if="line.selected_criteria && line.selected_criteria.length > 0"
                  variant="outlined" 
                  class="mt-3 pa-3"
                  color="success"
                >
                  <div class="d-flex align-center mb-2">
                    <v-icon color="success" class="mr-2">mdi-check-circle</v-icon>
                    <span class="text-subtitle-2 font-weight-medium">
                      Criterios Seleccionados {{ line.automatic_selection ? '(Automático - ' + line.strategy_applied?.toUpperCase() + ')' : '(Manual)' }}
                    </span>
                  </div>
                  
                  <div class="selected-criteria-list">
                    <v-chip
                      v-for="(criteria, idx) in line.selected_criteria"
                      :key="idx"
                      size="small"
                      class="mr-2 mb-1"
                      color="success"
                      variant="outlined"
                    >
                      <v-icon start>mdi-calendar</v-icon>
                      {{ criteria.display }} 
                      <span class="ml-1 font-weight-bold">({{ criteria.selected_quantity || criteria.available_quantity }} unidades)</span>
                    </v-chip>
                  </div>
                  
                  <div class="text-caption text-success mt-2">
                    Total disponible: {{ line.selected_criteria.reduce((sum, c) => sum + (c.selected_quantity || c.available_quantity), 0) }} unidades
                  </div>
                </v-card>
              </v-card-text>
            </v-card>

            <!-- Resumen -->
            <v-card class="mt-4" v-if="taskForm.task_lines.length > 0">
              <v-card-text>
                <v-row>
                  <v-col cols="12" md="8"></v-col>
                  <v-col cols="12" md="4">
                    <div class="text-right">
                      <div class="d-flex justify-space-between">
                        <span>Total Productos:</span>
                        <span>{{ taskForm.task_lines.length }}</span>
                      </div>
                      <div class="d-flex justify-space-between">
                        <span>Total Items:</span>
                        <span>{{ calculateTotalItems() }}</span>
                      </div>
                      <v-divider class="my-2"></v-divider>
                      <div class="d-flex justify-space-between text-h6 font-weight-bold">
                        <span>Tiempo Estimado:</span>
                        <span>{{ estimatedTime }} min</span>
                      </div>
                    </div>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </v-form>
        </v-card-text>
        
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" text @click="closeTaskDialog">
            Cancelar
          </v-btn>
          <v-btn
            color="primary"
            :loading="creatingTask"
            :disabled="taskForm.task_lines.length === 0"
            @click="createTask"
          >
            <v-icon class="mr-2">mdi-rocket-launch</v-icon>
            Crear Tarea
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog para detalles de tarea -->
    <v-dialog v-model="taskDetailsDialog" max-width="800px">
      <v-card v-if="selectedTask">
        <v-card-title>
          <v-icon class="mr-2">mdi-clipboard-list</v-icon>
          Detalles de Tarea #{{ selectedTask.task_number }}
        </v-card-title>
        <v-card-text>
          <v-row>
            <v-col cols="12" md="6">
              <v-list>
                <v-list-item>
                  <v-list-item-title>Estado</v-list-item-title>
                  <template v-slot:append>
                    <v-chip :color="getStatusColor(selectedTask.status)" dark small>
                      {{ getStatusText(selectedTask.status) }}
                    </v-chip>
                  </template>
                </v-list-item>
                <v-list-item>
                  <v-list-item-title>Estrategia</v-list-item-title>
                  <v-list-item-subtitle>{{ selectedTask.picking_strategy }}</v-list-item-subtitle>
                </v-list-item>
                <v-list-item>
                  <v-list-item-title>Tiempo Estimado</v-list-item-title>
                  <v-list-item-subtitle>{{ selectedTask.estimated_time }} minutos</v-list-item-subtitle>
                </v-list-item>
              </v-list>
            </v-col>
            <v-col cols="12" md="6">
              <v-list>
                <v-list-item>
                  <v-list-item-title>Prioridad</v-list-item-title>
                  <template v-slot:append>
                    <v-chip :color="getPriorityColor(selectedTask.priority)" dark small>
                      {{ getPriorityText(selectedTask.priority) }}
                    </v-chip>
                  </template>
                </v-list-item>
                <v-list-item>
                  <v-list-item-title>Tipo</v-list-item-title>
                  <v-list-item-subtitle>{{ selectedTask.picking_type }}</v-list-item-subtitle>
                </v-list-item>
                <v-list-item>
                  <v-list-item-title>Creado</v-list-item-title>
                  <v-list-item-subtitle>{{ formatDate(selectedTask.created_at) }}</v-list-item-subtitle>
                </v-list-item>
                <v-list-item>
                  <v-list-item-title>Picking Piece-by-Piece</v-list-item-title>
                  <template v-slot:append>
                    <v-chip 
                      :color="selectedTask.piece_by_piece_enabled ? 'primary' : 'grey'" 
                      size="small"
                      :prepend-icon="selectedTask.piece_by_piece_enabled ? 'mdi-barcode-scan' : 'mdi-package-variant'"
                    >
                      {{ selectedTask.piece_by_piece_enabled ? 'Habilitado' : 'Deshabilitado' }}
                    </v-chip>
                  </template>
                </v-list-item>
              </v-list>
            </v-col>
          </v-row>

          <!-- Productos de la tarea -->
          <v-divider class="my-4"></v-divider>
          <v-card-title class="text-h6">Productos a Recolectar</v-card-title>
          <v-table v-if="selectedTask.task_lines && selectedTask.task_lines.length > 0">
            <template v-slot:default>
              <thead>
                <tr>
                  <th>Producto</th>
                  <th>SKU</th>
                  <th>Cantidad</th>
                  <th>Unidad</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="line in selectedTask.task_lines" :key="line.id">
                  <td>{{ line.product_name }}</td>
                  <td>{{ line.product_sku }}</td>
                  <td>{{ line.quantity }}</td>
                  <td>{{ line.unit_of_measure }}</td>
                </tr>
              </tbody>
            </template>
          </v-table>

          <!-- Ruta de picking si está disponible -->
          <v-divider class="my-4" v-if="selectedTask.route && selectedTask.route.length > 0"></v-divider>
          <v-card-title class="text-h6" v-if="selectedTask.route && selectedTask.route.length > 0">Ruta de Picking Optimizada</v-card-title>
          <v-table v-if="selectedTask.route && selectedTask.route.length > 0">
            <template v-slot:default>
              <thead>
                <tr>
                  <th>Secuencia</th>
                  <th>Ubicación</th>
                  <th>Items</th>
                  <th>Cantidad Total</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="step in selectedTask.route" :key="step.sequence">
                  <td>{{ step.sequence }}</td>
                  <td>{{ step.location_code }}</td>
                  <td>{{ step.item_count }}</td>
                  <td>{{ step.total_quantity }}</td>
                </tr>
              </tbody>
            </template>
          </v-table>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" text @click="taskDetailsDialog = false">
            Cerrar
          </v-btn>
          <v-btn
            v-if="selectedTask.status === 'pending'"
            color="success"
            @click="startTask(selectedTask)"
          >
            <v-icon class="mr-2">mdi-play</v-icon>
            Iniciar Tarea
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog de Ejecución de Picking -->
    <v-dialog v-model="showExecutionDialog" max-width="1000px" persistent>
      <v-card>
        <v-card-title>
          <span class="text-h5">Ejecutando Picking - Tarea {{ executingTask?.task_number }}</span>
        </v-card-title>
        
        <v-card-text v-if="executingTask">
          <!-- Info de la tarea -->
          <v-row class="mb-4">
            <v-col cols="12" md="6">
              <v-chip :color="getStatusColor(executingTask.status)" class="mr-2">
                {{ getStatusText(executingTask.status) }}
              </v-chip>
              <v-chip 
                v-if="executingTask.piece_by_piece_enabled" 
                color="primary" 
                prepend-icon="mdi-barcode-scan"
                class="mr-2"
              >
                Picking Atomizado
              </v-chip>
            </v-col>
            <v-col cols="12" md="6">
              <div class="text-right">
                <div class="text-caption text-grey">Progreso General</div>
                <div class="text-h6">{{ getTaskProgress() }}%</div>
                <v-progress-linear 
                  :model-value="getTaskProgress()" 
                  color="primary"
                  height="6"
                ></v-progress-linear>
              </div>
            </v-col>
          </v-row>

          <!-- Items a recolectar -->
          <h3 class="mb-3">Items a Recolectar</h3>
          
          <ResponsiveDataTable
            :headers="executionHeaders"
            :items="executingTask.picking_items || []"
            class="items-table"
            item-key="id"
            hide-default-footer
          >
            <template v-slot:item.product_name="{ item }">
              <div>
                <div class="font-weight-medium">{{ item.product_name }}</div>
                <div class="text-caption text-grey">{{ item.product_sku }}</div>
              </div>
            </template>

            <template v-slot:item.status="{ item }">
              <v-chip :color="getItemStatusColor(item.status)" size="small">
                {{ getItemStatusText(item.status) }}
              </v-chip>
            </template>

            <template v-slot:item.quantity="{ item }">
              <div v-if="executingTask.piece_by_piece_enabled">
                <!-- Mostrar progreso de escaneos para piece-by-piece -->
                <div class="text-body-2">
                  <span :class="(item.quantity_scanned || 0) >= item.quantity_required ? 'text-success font-weight-bold' : ''">
                    {{ item.quantity_scanned || 0 }} / {{ item.quantity_required }}
                  </span>
                  <span class="text-caption text-grey ml-1">escaneados</span>
                </div>
                <div class="text-caption text-grey">
                  {{ item.quantity_picked || 0 }} / {{ item.quantity_required }} recolectados
                </div>
              </div>
              <div v-else>
                <!-- Mostrar solo recolectados para picking tradicional -->
                {{ item.quantity_picked || 0 }} / {{ item.quantity_required }}
              </div>
            </template>

            <template v-slot:item.actions="{ item }">
              <v-btn
                v-if="executingTask.status === 'in_progress' && item.status === 'pending'"
                color="success"
                size="small"
                @click="pickItem(item)"
                prepend-icon="mdi-check"
              >
                Recolectar
              </v-btn>
              
              <!-- Botón de escaneo para picking piece-by-piece -->
              <v-btn
                v-if="executingTask.status === 'in_progress' && 
                      item.status === 'pending' && 
                      executingTask.piece_by_piece_enabled &&
                      (item.quantity_scanned || 0) < item.quantity_required"
                color="primary"
                size="small"
                @click="startScanForItem(item)"
                prepend-icon="mdi-barcode-scan"
                class="mr-2"
              >
                Escanear
              </v-btn>
              
              <!-- Indicador de progreso de escaneo -->
              <v-chip
                v-if="executingTask.piece_by_piece_enabled && 
                      (item.quantity_scanned || 0) > 0 && 
                      (item.quantity_scanned || 0) < item.quantity_required"
                color="warning"
                size="small"
                prepend-icon="mdi-progress-clock"
                class="mr-2"
              >
                {{ item.quantity_scanned }}/{{ item.quantity_required }}
              </v-chip>
              
              <!-- Indicador de escaneado completo -->
              <v-chip
                v-if="executingTask.piece_by_piece_enabled && 
                      (item.quantity_scanned || 0) >= item.quantity_required"
                color="success"
                size="small"
                prepend-icon="mdi-check-circle"
                class="mr-2"
              >
                Completo
              </v-chip>
            </template>
          </ResponsiveDataTable>

          <!-- Panel de escaneo -->
          <v-card v-if="currentScanningItem" variant="outlined" class="mt-4">
            <v-card-title class="text-h6 py-3">
              <v-icon color="primary" class="mr-2">mdi-barcode-scan</v-icon>
              Escaneando: {{ currentScanningItem.product_name }}
            </v-card-title>
            <v-card-text>
              <v-row>
                <v-col cols="12" md="8">
                  <v-text-field
                    v-model="productBarcode"
                    label="Escanear código de barras"
                    placeholder="Usa el escáner o ingresa manualmente"
                    prepend-icon="mdi-barcode"
                    @keydown.enter="scanProductPiece"
                    ref="productBarcodeInput"
                    autofocus
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="4">
                  <v-btn 
                    color="primary" 
                    @click="scanProductPiece"
                    :disabled="!productBarcode.trim()"
                    class="mr-2"
                  >
                    <v-icon class="mr-1">mdi-check</v-icon>
                    Confirmar
                  </v-btn>
                  <v-btn 
                    color="grey" 
                    @click="cancelScan"
                  >
                    Cancelar
                  </v-btn>
                </v-col>
              </v-row>
              <div class="text-caption text-grey mt-2">
                Progreso: {{ currentScanningItem.quantity_scanned || 0 }}/{{ currentScanningItem.quantity_required }} unidades escaneadas
              </div>
            </v-card-text>
          </v-card>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn 
            color="grey" 
            @click="closeExecutionDialog"
            :disabled="executingTask?.status === 'in_progress'"
          >
            Cerrar
          </v-btn>
          <v-btn
            v-if="executingTask?.status === 'in_progress' && isTaskReadyToComplete()"
            color="success"
            @click="completePicking()"
            prepend-icon="mdi-check-circle"
          >
            Completar Picking
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar para notificaciones -->
    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="snackbar.timeout"
      top
    >
      {{ snackbar.message }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar.show = false">
          Cerrar
        </v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
import api from '@/api'
import ResponsiveDataTable from '@/components/ResponsiveDataTable.vue'
import PickingStrategySelector from '@/components/PickingStrategySelector.vue'
import PickingCriteriaSelector from '@/components/PickingCriteriaSelector.vue'

export default {
  name: 'IntelligentPicking',
  components: {
    ResponsiveDataTable,
    PickingStrategySelector,
    PickingCriteriaSelector
  },
  data() {
    return {
      // Estados de carga
      loading: false,
      creatingTask: false,
      
      // Estadísticas
      stats: {
        activeTasks: 0,
        avgTime: 0,
        efficiency: 95,
        itemsPicked: 1247
      },
      
      // Datos maestros
      products: [],
      productsWithInventory: [],
      filteredProductsByTaskLineIndex: {},
      
      // Control de saldos de criterios usados entre líneas
      usedCriteriaSaldos: {},
      
      // Picking strategy
      pickingStrategy: {
        level: 'order', // 'order' o 'product'
        strategy: null   // 'fifo', 'fefo', 'lifo', 'manual', null
      },
      pickingStrategyOptions: [],
      usedLotsByProduct: {}, // Para rastrear lotes ya usados
      strategies: [
        { value: 'fifo', name: 'FIFO', description: 'First In, First Out - Primero en entrar, primero en salir' },
        { value: 'fefo', name: 'FEFO', description: 'First Expired, First Out - Primero en vencer, primero en salir' },
        { value: 'lifo', name: 'LIFO', description: 'Last In, First Out - Último en entrar, primero en salir' },
        { value: 'manual', name: 'MANUAL', description: 'Selección Manual - Permite elegir ubicaciones específicas' }
      ],
      priorities: [
        { value: 1, name: 'LOW', description: 'Baja - Picking rutinario' },
        { value: 2, name: 'NORMAL', description: 'Normal - Picking estándar' },
        { value: 3, name: 'HIGH', description: 'Alta - Picking prioritario' },
        { value: 4, name: 'URGENT', description: 'Urgente - Requiere atención inmediata' },
        { value: 5, name: 'CRITICAL', description: 'Crítico - Máxima prioridad' }
      ],
      
      // Tareas
      tasks: [],
      
      // Filtros
      filters: {
        status: null,
        strategy: null,
        date_from: null,
        date_to: null
      },
      
      // Headers de tabla
      taskHeaders: [
        { title: 'Número', key: 'task_number', sortable: true },
        { title: 'Estado', key: 'status', sortable: true },
        { title: 'Estrategia', key: 'picking_strategy', sortable: true },
        { title: 'Prioridad', key: 'priority', sortable: true },
        { title: 'Piece-by-Piece', key: 'piece_by_piece_enabled', sortable: true },
        { title: 'Tiempo Est.', key: 'estimated_time', sortable: true },
        { title: 'Creado', key: 'created_at', sortable: true },
        { title: 'Acciones', key: 'actions', sortable: false }
      ],
      
      executionHeaders: [
        { title: 'Producto', key: 'product_name', sortable: false },
        { title: 'Estado', key: 'status', sortable: false },
        { title: 'Cantidad', key: 'quantity', sortable: false },
        { title: 'Ubicación', key: 'location', sortable: false },
        { title: 'Acciones', key: 'actions', sortable: false }
      ],
      
      // Formulario de tarea
      showCreateTaskDialog: false,
      editingTask: null,
      taskForm: {
        picking_type: null,
        strategy: null,
        priority: 2,
        required_date: null,
        notes: '',
        task_lines: [],
        piece_by_piece_enabled: false
      },
      
      // Dialogs
      taskDetailsDialog: false,
      selectedTask: null,
      
      // Execution Dialog
      showExecutionDialog: false,
      executingTask: null,
      currentScanningItem: null,
      productBarcode: '',
      showBarcodeScanner: false,
      
      // Opciones
      statusOptions: [
        { text: 'Pendiente', value: 'pending' },
        { text: 'Asignado', value: 'assigned' },
        { text: 'Recolectando', value: 'picking' },
        { text: 'Recolectado', value: 'picked' },
        { text: 'Empacado', value: 'packed' },
        { text: 'Pickeado', value: 'shipped' },
        { text: 'Entregado', value: 'delivered' },
        { text: 'Cancelado', value: 'cancelled' }
      ],
      
      pickingTypeOptions: [
        { text: 'Individual', value: 'single' },
        { text: 'Por Lotes', value: 'batch' },
        { text: 'Por Olas', value: 'wave' }
      ],
      
      // Notificaciones
      snackbar: {
        show: false,
        message: '',
        color: 'success',
        timeout: 4000
      },
      
      estimatedTime: 0
    }
  },
  
  computed: {
    strategyOptions() {
      return this.strategies.map(strategy => ({
        text: strategy.description || strategy.name,
        value: strategy.value
      }))
    },
    
    priorityOptions() {
      return this.priorities.map(priority => ({
        text: priority.description || priority.name,
        value: priority.value
      }))
    }
  },
  
  async created() {
    await this.loadInitialData()
  },
  
  methods: {
    async loadInitialData() {
      try {
        await Promise.all([
          this.loadProducts(),
          this.loadPickingStrategies(),
          this.loadPriorities(),
          this.loadTasks()
        ])
        this.updateStats()
      } catch (error) {
        this.showError('Error al cargar datos iniciales')
      }
    },
    
    async loadProducts() {
      try {
        const response = await api.getProductsWithInventory()
        this.productsWithInventory = response.data
        this.products = response.data // Mantener compatibilidad
        console.log('Productos cargados:', this.products.length)
      } catch (error) {
        console.error('Error loading products:', error)
        this.showError('Error al cargar productos')
      }
    },
    
    async loadPickingStrategies() {
      try {
        const response = await api.getPickingStrategies()
        if (response.data && response.data.strategies) {
          this.pickingStrategyOptions = response.data.strategies.map(strategy => ({
            value: strategy.value,
            text: strategy.name,
            description: strategy.description
          }))
        }
      } catch (error) {
        console.error('Error loading picking strategies:', error)
        // Usar estrategias por defecto si falla la carga
        this.pickingStrategyOptions = [
          { value: 'fifo', text: 'FIFO', description: 'First In, First Out' },
          { value: 'fefo', text: 'FEFO', description: 'First Expired, First Out' },
          { value: 'lifo', text: 'LIFO', description: 'Last In, First Out' },
          { value: 'manual', text: 'Manual', description: 'Selección manual de criterios' }
        ]
      }
    },
    
    async loadStrategies() {
      try {
        const response = await api.getPickingStrategies()
        if (response.data && response.data.strategies && response.data.strategies.length > 0) {
          this.strategies = response.data.strategies
        }
      } catch (error) {
        console.error('Error loading strategies:', error)
        // Usar estrategias por defecto
      }
    },
    
    async loadPriorities() {
      try {
        const response = await api.getPickingPriorities()
        if (response.data && response.data.priorities && response.data.priorities.length > 0) {
          this.priorities = response.data.priorities
        }
      } catch (error) {
        console.error('Error loading priorities:', error)
        // Usar prioridades por defecto
      }
    },
    
    async loadTasks() {
      this.loading = true
      try {
        // Preparar parámetros de filtro
        const params = {}
        
        if (this.filters.status) {
          params.status = this.filters.status
        }
        
        if (this.filters.strategy) {
          params.strategy = this.filters.strategy
        }
        
        if (this.filters.date_from) {
          params.date_from = this.filters.date_from
        }
        
        if (this.filters.date_to) {
          params.date_to = this.filters.date_to
        }
        
        const response = await api.getPickingTasks(params)
        this.tasks = response.data.tasks || []
        this.updateStats()
      } catch (error) {
        this.showError('Error al cargar tareas')
        console.error('Error loading tasks:', error)
      } finally {
        this.loading = false
      }
    },
    
    updateStats() {
      // Tareas activas (pendientes, asignadas, en progreso)
      const activeTasks = this.tasks.filter(task => 
        ['pending', 'assigned', 'picking'].includes(task.status)
      ).length
      
      // Tareas completadas para calcular estadísticas
      const completedTasks = this.tasks.filter(task => 
        ['completed', 'delivered', 'shipped', 'picked'].includes(task.status)
      )
      
      // Función para extraer tiempo real de las notas
      const extractActualTime = (task) => {
        if (task.notes && task.notes.includes('actual_time:')) {
          const match = task.notes.match(/actual_time:(\d+)/)
          return match ? parseInt(match[1]) : null
        }
        return null
      }
      
      // Tiempo promedio de tareas completadas
      let avgTime = 0
      if (completedTasks.length > 0) {
        const totalTime = completedTasks.reduce((sum, task) => {
          // Usar tiempo real si está disponible, sino el estimado
          const actualTime = extractActualTime(task)
          return sum + (actualTime || task.estimated_time || 0)
        }, 0)
        avgTime = Math.round(totalTime / completedTasks.length)
      }
      
      // Eficiencia: comparar tiempo real vs estimado
      let efficiency = 95 // Valor por defecto
      const tasksWithBothTimes = completedTasks.filter(task => {
        const actualTime = extractActualTime(task)
        return actualTime && task.estimated_time && task.estimated_time > 0
      })
      
      if (tasksWithBothTimes.length > 0) {
        const totalEstimated = tasksWithBothTimes.reduce((sum, task) => sum + task.estimated_time, 0)
        const totalActual = tasksWithBothTimes.reduce((sum, task) => {
          const actualTime = extractActualTime(task)
          return sum + actualTime
        }, 0)
        
        if (totalActual > 0) {
          // Eficiencia = (tiempo estimado / tiempo real) * 100
          // Si es mayor a 100%, significa que se hizo más rápido de lo estimado
          efficiency = Math.round((totalEstimated / totalActual) * 100)
          // Limitar entre 0 y 150% para valores razonables
          efficiency = Math.min(150, Math.max(0, efficiency))
        }
      }
      
      // Items procesados: sumar cantidades de todas las líneas de tareas completadas
      let itemsProcessed = 0
      completedTasks.forEach(task => {
        if (task.task_lines && task.task_lines.length > 0) {
          itemsProcessed += task.task_lines.reduce((sum, line) => 
            sum + (line.quantity_picked || line.quantity || 0), 0
          )
        }
      })
      
      // Si no hay tareas completadas, mostrar items de tareas en progreso
      if (itemsProcessed === 0) {
        const inProgressTasks = this.tasks.filter(task => 
          ['picking', 'picked'].includes(task.status)
        )
        inProgressTasks.forEach(task => {
          if (task.task_lines && task.task_lines.length > 0) {
            itemsProcessed += task.task_lines.reduce((sum, line) => 
              sum + (line.quantity_picked || 0), 0
            )
          }
        })
      }
      
      this.stats = {
        activeTasks,
        avgTime,
        efficiency,
        itemsPicked: itemsProcessed
      }
      
      console.log('Estadísticas actualizadas:', {
        activeTasks,
        avgTime,
        efficiency,
        itemsProcessed,
        completedTasksCount: completedTasks.length,
        tasksWithBothTimesCount: tasksWithBothTimes.length
      })
    },
    
    // Gestión de líneas de tarea
    addTaskLine() {
      const index = this.taskForm.task_lines.length
      this.taskForm.task_lines.push({
        product_id: null,
        quantity: 1,
        unit_of_measure: '',
        max_quantity: null,
        productSearch: '',
        // Campos para estrategia de picking
        picking_strategy: null,
        picking_criteria: {
          type: null,
          value: null,
          available_quantity: null
        },
        // Nuevos campos para selección múltiple y estrategias automáticas
        selected_criteria: [],
        automatic_selection: false,
        strategy_applied: null,
        // Datos disponibles para selectores
        available_lots: [],
        available_serials: [],
        available_expiry_dates: [],
        has_any_criteria: false,
        can_select_criteria: true,
        criteria_error: null
      })
      // Inicializar el filtro para esta línea
      this.filteredProductsByTaskLineIndex[index] = this.productsWithInventory
    },
    
    removeTaskLine(index) {
      this.taskForm.task_lines.splice(index, 1)
      
      // Limpiar el filtro para este índice
      delete this.filteredProductsByTaskLineIndex[index]
      
      // Reindexar los filtros restantes
      const newFilters = {}
      Object.keys(this.filteredProductsByTaskLineIndex).forEach(key => {
        const keyIndex = parseInt(key)
        if (keyIndex > index) {
          newFilters[keyIndex - 1] = this.filteredProductsByTaskLineIndex[key]
        } else if (keyIndex < index) {
          newFilters[keyIndex] = this.filteredProductsByTaskLineIndex[key]
        }
      })
      this.filteredProductsByTaskLineIndex = newFilters
      
      this.calculateEstimatedTime()
    },
    
    async updateLineProduct(line, productId) {
      const product = this.productsWithInventory.find(p => p.id === productId)
      if (product) {
        line.unit_of_measure = product.unit_of_measure
        line.max_quantity = product.available_quantity
        line.product_name = product.name
        line.product_sku = product.sku
        
        // Validar cantidad máxima disponible
        if (line.quantity > product.available_quantity) {
          line.quantity = product.available_quantity
        }
        
        // Limpiar criterios anteriores
        this.clearLineCriteria(line)
        
        // Cargar criterios disponibles para este producto
        await this.loadAvailableItemsForTaskLine(line, -1, productId)
        
        // Aplicar estrategia si ya está seleccionada
        if (this.pickingStrategy.level === 'order' && this.pickingStrategy.strategy) {
          // Pequeño delay para asegurar que los datos se cargaron
          setTimeout(() => {
            this.applyAutomaticStrategyForTask(line, this.pickingStrategy.strategy, -1)
          }, 100)
        } else if (this.pickingStrategy.level === 'product' && line.picking_strategy) {
          setTimeout(() => {
            this.applyAutomaticStrategyForTask(line, line.picking_strategy, -1)
          }, 100)
        }
      }
      this.calculateEstimatedTime()
    },
    
    // Obtener hint para el campo de cantidad
    getQuantityHint(line) {
      if (!line.max_quantity) return ''
      
      // Si hay criterios múltiples seleccionados, mostrar información específica
      if (line.selected_criteria && line.selected_criteria.length > 0) {
        const totalAvailable = line.selected_criteria.reduce((sum, criteria) => sum + criteria.available_quantity, 0)
        return `Total disponible en criterios seleccionados: ${totalAvailable} unidades`
      }
      
      // Si hay un criterio seleccionado, mostrar información específica
      if (line.picking_criteria && line.picking_criteria.type && line.picking_criteria.value) {
        const criteriaType = line.picking_criteria.type === 'lot' ? 'Lote' :
                           line.picking_criteria.type === 'serial' ? 'S/N' :
                           line.picking_criteria.type === 'expiry' ? 'Vencimiento' : 'Criterio'
        return `Máximo para ${criteriaType} seleccionado: ${line.max_quantity} unidades`
      }
      
      // Hint normal cuando no hay criterio específico
      return `Máximo disponible: ${line.max_quantity} unidades`
    },
    
    // Métodos de estrategia de picking (adaptados de Orders.vue)
    showCriteriaSelectors(line, index) {
      // Estrategia por orden/tarea: mostrar siempre para poder ver las fechas automáticas seleccionadas
      if (this.pickingStrategy.level === 'order') {
        return this.pickingStrategy.strategy && line.product_id && line.has_any_criteria
      }
      
      // Estrategia por producto: mostrar siempre para permitir selección múltiple
      if (this.pickingStrategy.level === 'product') {
        return line.product_id && line.has_any_criteria
      }
      
      return false
    },

    // Manejar cambios en la estrategia de picking
    onPickingStrategyChanged(event) {
      console.log('🎯 Picking strategy changed:', event)
      
      if (event.type === 'level-changed') {
        if (event.level === 'order') {
          // Limpiar estrategias individuales de productos
          this.taskForm.task_lines.forEach(line => {
            line.picking_strategy = null
            this.clearLineCriteria(line)
          })
        } else if (event.level === 'product') {
          // Limpiar criterios de todas las líneas para permitir selección individual
          this.taskForm.task_lines.forEach(line => {
            this.clearLineCriteria(line)
          })
        }
      }
      
      if (event.type === 'strategy-changed' && event.level === 'order') {
        // Aplicar estrategia automática a todos los productos 
        if (event.strategy) {
          this.taskForm.task_lines.forEach((line, index) => {
            if (line.product_id) {
              this.applyAutomaticStrategyForTask(line, event.strategy, index)
            }
          })
        } else {
          // Si se limpia, limpiar criterios
          this.taskForm.task_lines.forEach(line => {
            this.clearLineCriteria(line)
          })
        }
      }
    },

    // Cargar items disponibles cuando se selecciona un producto
    async loadAvailableItemsForTaskLine(line, lineIndex, productId) {
      if (!productId) return
      
      try {
        console.log(`🔍 Loading available items for product ${productId}`)
        const response = await api.getProductAvailableItems(productId)
        const data = response.data
        
        // Poblar línea con datos disponibles
        this.populateLineWithAvailableItems(line, data)
        
        // Validar línea para estrategia
        this.validateLineForStrategy(line, lineIndex)
        
        console.log(`✅ Available items loaded for line ${lineIndex}:`, {
          lots: line.available_lots.length,
          serials: line.available_serials.length,
          expiry_dates: line.available_expiry_dates.length,
          has_any_criteria: line.has_any_criteria
        })
        
      } catch (error) {
        console.error('Error loading available items:', error)
        this.showError('Error al cargar criterios de trazabilidad para el producto')
      }
    },

    populateLineWithAvailableItems(line, data) {
      console.log(`📦 Populating line with available items:`, data)
      
      // Preservar criterio seleccionado previamente
      const previousCriteria = { ...line.picking_criteria }
      
      line.available_lots = data.lots || []
      line.available_serials = data.serials || []
      line.available_expiry_dates = data.expiry_dates || []
      line.has_any_criteria = data.has_any_criteria || false
      
      console.log(`📦 Line populated - has_any_criteria: ${line.has_any_criteria}, lots: ${line.available_lots.length}, serials: ${line.available_serials.length}, expiry: ${line.available_expiry_dates.length}`)
      
      // Restaurar criterio seleccionado si existía
      if (previousCriteria.type && previousCriteria.value) {
        line.picking_criteria = previousCriteria
        console.log(`🔄 Restored previous criteria:`, previousCriteria)
        
        // Actualizar cantidad máxima basada en el criterio seleccionado
        this.updateMaxQuantityForCriteria(line, previousCriteria)
      }
      
      // Marcar lotes ya usados como deshabilitados
      if (line.product_id && this.usedLotsByProduct[line.product_id]) {
        line.available_lots.forEach(lot => {
          lot.disabled = this.usedLotsByProduct[line.product_id].includes(lot.value)
        })
      }
    },

    updateMaxQuantityForCriteria(line, criteria) {
      let availableList = []
      
      switch (criteria.type) {
        case 'lot':
          availableList = line.available_lots
          break
        case 'serial':
          availableList = line.available_serials
          break
        case 'expiry':
          availableList = line.available_expiry_dates
          break
      }
      
      const selectedItem = availableList.find(item => item.value === criteria.value)
      if (selectedItem) {
        line.max_quantity = selectedItem.available_quantity
        console.log(`📊 Updated max quantity for criteria ${criteria.type}=${criteria.value}: ${selectedItem.available_quantity}`)
      }
    },

    // Validar línea para estrategia
    validateLineForStrategy(line, index) {
      // Determinar la estrategia efectiva (global o individual)
      const strategy = this.pickingStrategy.level === 'order' 
        ? this.pickingStrategy.strategy 
        : line.picking_strategy
      
      // Si no hay estrategia definida, no mostrar error
      if (!strategy) {
        line.can_select_criteria = true
        line.criteria_error = null
        return true
      }
      
      if (strategy === 'fefo' && line.available_expiry_dates.length === 0) {
        line.can_select_criteria = false
        line.criteria_error = 'Este producto no tiene fechas de vencimiento para estrategia FEFO'
        return false
      }
      
      if ((strategy === 'fifo' || strategy === 'lifo') && !line.has_any_criteria) {
        line.can_select_criteria = false
        line.criteria_error = 'Este producto no tiene criterios de trazabilidad'
        return false
      }
      
      if (strategy === 'manual' && !line.has_any_criteria) {
        line.can_select_criteria = false
        line.criteria_error = 'Este producto no tiene criterios para selección manual'
        return false
      }
      
      line.can_select_criteria = true
      line.criteria_error = null
      return true
    },

    // Aplicar estrategia automática
    applyAutomaticStrategy(line, strategy, lineIndex = null) {
      this.clearLineCriteria(line)
      
      switch (strategy) {
        case 'fifo':
          this.applyFifoStrategy(line)
          break
        case 'fefo':
          this.applyFefoStrategy(line)
          break
        case 'lifo':
          this.applyLifoStrategy(line)
          break
      }
    },

    applyFifoStrategy(line) {
      // Seleccionar el lote más antiguo o el serial más bajo
      if (line.available_lots.length > 0) {
        const oldestLot = line.available_lots[0] // Ya están ordenados por fecha
        line.picking_criteria = {
          type: 'lot',
          value: oldestLot.value,
          available_quantity: oldestLot.available_quantity
        }
        line.max_quantity = oldestLot.available_quantity
      } else if (line.available_serials.length > 0) {
        const firstSerial = line.available_serials[0]
        line.picking_criteria = {
          type: 'serial',
          value: firstSerial.value,
          available_quantity: firstSerial.available_quantity
        }
        line.max_quantity = firstSerial.available_quantity
      }
    },

    applyFefoStrategy(line) {
      // Seleccionar el lote que vence más pronto
      if (line.available_expiry_dates.length > 0) {
        const earliestExpiry = line.available_expiry_dates[0] // Ya están ordenados por fecha de vencimiento
        line.picking_criteria = {
          type: 'expiry',
          value: earliestExpiry.value,
          available_quantity: earliestExpiry.available_quantity
        }
        line.max_quantity = earliestExpiry.available_quantity
      }
    },

    applyLifoStrategy(line) {
      // Seleccionar el lote más reciente
      if (line.available_lots.length > 0) {
        const newestLot = line.available_lots[line.available_lots.length - 1]
        line.picking_criteria = {
          type: 'lot',
          value: newestLot.value,
          available_quantity: newestLot.available_quantity
        }
        line.max_quantity = newestLot.available_quantity
      }
    },

    clearLineCriteria(line) {
      line.picking_criteria = {
        type: null,
        value: null,
        available_quantity: null
      }
      // No resetear max_quantity aquí para mantener el stock disponible del producto
    },

    onCriteriaChanged(line, index, event) {
      console.log(`🎯 Criteria changed for line ${index}:`, event)
      
      if (event.criteria && event.criteria.available_quantity) {
        line.max_quantity = event.criteria.available_quantity
        
        // Ajustar cantidad si excede el máximo disponible
        if (line.quantity > line.max_quantity) {
          line.quantity = line.max_quantity
        }
      }
    },

    onLineStrategyChanged(line, index, event) {
      console.log(`🎯 Line strategy changed for line ${index}:`, event)
      
      if (event.strategy && event.strategy !== 'manual') {
        this.applyAutomaticStrategy(line, event.strategy, index)
      } else {
        this.clearLineCriteria(line)
      }
    },
    
    calculateTotalItems() {
      return this.taskForm.task_lines.reduce((total, line) => total + (line.quantity || 0), 0)
    },
    
    calculateEstimatedTime() {
      // Estimación simple: 2 minutos por producto + 0.5 minutos por item
      const baseTime = this.taskForm.task_lines.length * 2
      const itemTime = this.calculateTotalItems() * 0.5
      this.estimatedTime = Math.round(baseTime + itemTime)
    },
    
    // Filtrado de productos
    getFilteredProductsForTaskLine(index) {
      return this.filteredProductsByTaskLineIndex[index] || this.productsWithInventory
    },
    
    handleProductSearchForTaskLine(search, index) {
      const line = this.taskForm.task_lines[index]
      if (line) {
        line.productSearch = search
      }
      
      if (!search || search.length < 2) {
        this.filteredProductsByTaskLineIndex[index] = this.productsWithInventory
        return
      }
      
      const filtered = this.productsWithInventory.filter(product => {
        const searchLower = search.toLowerCase()
        const skuMatch = product.sku && product.sku.toLowerCase().includes(searchLower)
        const nameMatch = product.name && product.name.toLowerCase().includes(searchLower)
        return skuMatch || nameMatch
      })
      
      this.filteredProductsByTaskLineIndex[index] = filtered
      
      // Auto-seleccionar si hay exactamente 1 resultado y búsqueda >= 4 caracteres
      if (filtered.length === 1 && search.length >= 4 && line) {
        setTimeout(() => {
          line.product_id = filtered[0].id
          this.updateLineProduct(line, filtered[0].id)
        }, 100)
      }
    },
    
    // Acciones de tarea
    async createTask() {
      if (this.taskForm.task_lines.length === 0) return
      
      this.creatingTask = true
      try {
        const taskData = {
          picking_type: this.taskForm.picking_type,
          strategy: this.taskForm.strategy,
          priority: this.taskForm.priority,
          required_date: this.taskForm.required_date,
          notes: this.taskForm.notes,
          task_lines: this.taskForm.task_lines.map(line => ({
            product_id: line.product_id,
            quantity: line.quantity
          })),
          piece_by_piece_enabled: this.taskForm.piece_by_piece_enabled,
          // Agregar información de picking granular
          picking_strategy_config: {
            level: this.pickingStrategy.level,
            strategy: this.pickingStrategy.strategy
          },
          granular_criteria: {}
        }
        
        // Agregar criterios granulares por línea
        this.taskForm.task_lines.forEach((line, index) => {
          console.log(`🔍 Task Line ${index} data:`, {
            product_id: line.product_id,
            picking_strategy: line.picking_strategy,
            picking_criteria: line.picking_criteria,
            has_any_criteria: line.has_any_criteria
          })
          
          if (line.picking_criteria && (line.picking_criteria.type || line.picking_strategy)) {
            taskData.granular_criteria[index] = {
              picking_strategy: line.picking_strategy,
              type: line.picking_criteria.type,
              value: line.picking_criteria.value,
              available_quantity: line.picking_criteria.available_quantity
            }
            console.log(`✅ Added granular criteria for task line ${index}:`, taskData.granular_criteria[index])
          } else {
            console.log(`❌ No granular criteria for task line ${index} - missing data`)
          }
        })
        
        console.log('📦 Creating task with granular criteria:', taskData.granular_criteria)
        console.log('🎯 Picking strategy config:', taskData.picking_strategy_config)
        
        const response = await api.createPickingTask(taskData)
        
        this.showSuccess('Tarea creada exitosamente')
        this.closeTaskDialog()
        await this.loadTasks()
        
      } catch (error) {
        console.error('Error creating task:', error)
        this.showError('Error al crear tarea de picking')
      } finally {
        this.creatingTask = false
      }
    },
    
    closeTaskDialog() {
      this.showCreateTaskDialog = false
      this.editingTask = null
      this.resetTaskForm()
    },
    
    resetTaskForm() {
      this.taskForm = {
        picking_type: null,
        strategy: null,
        priority: 2,
        required_date: null,
        notes: '',
        task_lines: [],
        piece_by_piece_enabled: false
      }
      
      // Resetear estrategia de picking
      this.pickingStrategy = {
        level: 'order',
        strategy: null
      }
      
      // Limpiar lotes usados y saldos de criterios
      this.usedLotsByProduct = {}
      this.usedCriteriaSaldos = {}
      
      // Limpiar filtros de productos
      this.filteredProductsByTaskLineIndex = {}
      
      this.estimatedTime = 0
    },
    
    // Acciones de tabla
    viewTask(task) {
      this.selectedTask = task
      this.taskDetailsDialog = true
    },
    
    editTask(task) {
      this.editingTask = task
      // Cargar datos del task al formulario
      this.taskForm = {
        picking_type: task.notes?.split('|')[1] || 'single',
        strategy: task.shipping_method || 'fifo',
        priority: task.priority || 2,
        required_date: task.required_date ? task.required_date.slice(0, 16) : null,
        notes: task.notes?.split('|')[2] || '',
        task_lines: task.task_lines || [],
        piece_by_piece_enabled: task.piece_by_piece_enabled || false
      }
      this.showCreateTaskDialog = true
    },
    
    // Métodos de utilidad
    getStatusColor(status) {
      const colors = {
        'pending': 'orange',
        'assigned': 'blue',
        'picking': 'purple',
        'picked': 'indigo',
        'packed': 'teal',
        'shipped': 'green',
        'delivered': 'success',
        'cancelled': 'red',
        // Estados legacy para compatibilidad
        'in_progress': 'purple',
        'completed': 'green'
      }
      return colors[status] || 'grey'
    },
    
    getStatusText(status) {
      const texts = {
        'pending': 'Pendiente',
        'assigned': 'Asignado',
        'picking': 'Recolectando',
        'picked': 'Recolectado',
        'packed': 'Empacado',
        'shipped': 'Pickeado',
        'delivered': 'Entregado',
        'cancelled': 'Cancelado',
        // Estados legacy para compatibilidad
        'in_progress': 'En Progreso',
        'completed': 'Completado'
      }
      return texts[status] || status
    },
    
    getStrategyColor(strategy) {
      const colors = {
        'fifo': 'blue',
        'fefo': 'green',
        'lifo': 'orange',
        'manual': 'purple'
      }
      return colors[strategy?.toLowerCase()] || 'grey'
    },
    
    getPriorityColor(priority) {
      const colors = {
        1: 'grey',
        2: 'blue',
        3: 'orange',
        4: 'red',
        5: 'purple'
      }
      return colors[priority] || 'grey'
    },
    
    getPriorityText(priority) {
      const texts = {
        1: 'Baja',
        2: 'Normal',
        3: 'Alta',
        4: 'Urgente',
        5: 'Crítica'
      }
      return texts[priority] || 'Normal'
    },
    
    formatDate(dateString) {
      if (!dateString) return '-'
      return new Date(dateString).toLocaleString()
    },
    
    showSuccess(message) {
      this.snackbar = {
        show: true,
        message,
        color: 'success',
        timeout: 4000
      }
    },
    
    showError(message) {
      this.snackbar = {
        show: true,
        message,
        color: 'error',
        timeout: 6000
      }
    },

    async allocateTask(task) {
      try {
        const response = await api.allocateOrder(task.id)
        if (response.data.fully_allocated) {
          this.showSuccess('Inventario asignado exitosamente')
        } else {
          this.showSuccess('Asignación parcial completada')
        }
        await this.loadTasks()
        this.updateStats()
      } catch (error) {
        this.showError('Error al asignar inventario')
        console.error('Error allocating task:', error)
      }
    },

    async createPickingList(task) {
      try {
        await api.createPickingList(task.id)
        this.showSuccess('Lista de picking creada exitosamente')
        await this.loadTasks()
        this.updateStats()
      } catch (error) {
        this.showError('Error al crear lista de picking')
        console.error('Error creating picking list:', error)
      }
    },

    async createShipment(task) {
      try {
        const shipmentData = {
          carrier: 'Transportista Estándar',
          tracking_number: `TRK${Date.now()}`,
          service_type: 'standard'
        }
        await api.createShipment(task.id, shipmentData)
        this.showSuccess('Envío creado exitosamente')
        await this.loadTasks()
        this.updateStats()
      } catch (error) {
        this.showError('Error al crear envío')
        console.error('Error creating shipment:', error)
      }
    },

    async releaseAllocations(task) {
      try {
        await api.releaseAllocations(task.id)
        this.showSuccess('Asignaciones liberadas exitosamente')
        await this.loadTasks()
        this.updateStats()
      } catch (error) {
        this.showError('Error al liberar asignaciones')
        console.error('Error releasing allocations:', error)
      }
    },

    // === MÉTODOS DE EJECUCIÓN DE PICKING ATOMIZADO ===
    
    async startTask(task) {
      try {
        // Iniciar la tarea en el backend
        await api.startPickingList(task.id)
        
        // Cargar detalles completos de la tarea
        const response = await api.getPickingList(task.id)
        this.executingTask = response.data
        
        this.showExecutionDialog = true
        this.showSuccess('Tarea iniciada exitosamente')
        
        // Actualizar lista de tareas
        await this.loadTasks()
        
        // Cerrar el dialog de detalles si está abierto
        this.taskDetailsDialog = false
        
      } catch (error) {
        console.error('Error starting task:', error)
        this.showError('Error al iniciar la tarea')
      }
    },
    
    closeExecutionDialog() {
      this.showExecutionDialog = false
      this.executingTask = null
      this.currentScanningItem = null
      this.productBarcode = ''
    },
    
    getTaskProgress() {
      if (!this.executingTask?.picking_items || this.executingTask.picking_items.length === 0) {
        return 0
      }
      
      const items = this.executingTask.picking_items
      if (this.executingTask.piece_by_piece_enabled) {
        // Para picking atomizado, considerar tanto escaneos como picking completado
        const totalRequired = items.reduce((sum, item) => sum + item.quantity_required, 0)
        const totalScanned = items.reduce((sum, item) => sum + (item.quantity_scanned || 0), 0)
        return Math.round((totalScanned / totalRequired) * 100)
      } else {
        // Para picking tradicional, solo considerar items completados
        const completedItems = items.filter(item => item.status === 'picked').length
        return Math.round((completedItems / items.length) * 100)
      }
    },
    
    isTaskReadyToComplete() {
      if (!this.executingTask?.picking_items) return false
      
      return this.executingTask.picking_items.every(item => {
        if (this.executingTask.piece_by_piece_enabled) {
          // Para picking atomizado, verificar que estén escaneados y recolectados
          return (item.quantity_scanned || 0) >= item.quantity_required && 
                 item.status === 'picked'
        } else {
          // Para picking tradicional, solo verificar que estén recolectados
          return item.status === 'picked'
        }
      })
    },
    
    async pickItem(item) {
      try {
        // Verificar si requiere escaneo y no se ha escaneado completamente
        if (this.executingTask.piece_by_piece_enabled && 
            (item.quantity_scanned || 0) < item.quantity_required) {
          this.showError('Debe escanear todas las unidades antes de completar el picking')
          return
        }
        
        // Marcar item como recolectado
        await api.pickItem(item.id, {
          quantity_picked: item.quantity_required,
          status: 'picked'
        })
        
        this.showSuccess(`Item ${item.product_name} recolectado`)
        
        // Actualizar los datos de la tarea
        const response = await api.getPickingList(this.executingTask.id)
        this.executingTask = response.data
        
      } catch (error) {
        if (error.response?.data?.requires_scan) {
          this.showError('Debe escanear el código de barras antes de completar el picking')
        } else {
          this.showError('Error al recolectar item')
        }
        console.error('Error picking item:', error)
      }
    },
    
    startScanForItem(item) {
      this.currentScanningItem = item
      this.productBarcode = ''
      
      // Enfocar el input de código de barras
      this.$nextTick(() => {
        if (this.$refs.productBarcodeInput) {
          this.$refs.productBarcodeInput.focus()
        }
      })
    },
    
    async scanProductPiece() {
      if (!this.productBarcode.trim() || !this.currentScanningItem) return
      
      try {
        const response = await api.scanPickingProductPiece(
          this.currentScanningItem.id, 
          this.productBarcode.trim()
        )
        
        if (response.data.success) {
          // Actualizar los datos del item
          const itemIndex = this.executingTask.picking_items.findIndex(
            item => item.id === this.currentScanningItem.id
          )
          if (itemIndex !== -1) {
            this.executingTask.picking_items[itemIndex] = response.data.item
          }
          
          this.productBarcode = ''
          
          if (response.data.all_scanned) {
            this.showSuccess(`¡Todas las unidades de ${this.currentScanningItem.product_name} han sido escaneadas!`)
            this.currentScanningItem = null
          } else {
            this.showSuccess(response.data.message || 'Unidad escaneada correctamente')
            // Mantener el foco para el siguiente escaneo
            this.$nextTick(() => {
              if (this.$refs.productBarcodeInput) {
                this.$refs.productBarcodeInput.focus()
              }
            })
          }
        }
        
      } catch (error) {
        console.error('Error scanning product piece:', error)
        if (error.response?.data?.message) {
          this.showError(error.response.data.message)
        } else {
          this.showError('Error al escanear el producto')
        }
      }
    },
    
    cancelScan() {
      this.currentScanningItem = null
      this.productBarcode = ''
    },
    
    async completePicking() {
      try {
        await api.completePickingList(this.executingTask.id)
        this.showSuccess('Picking completado exitosamente')
        this.closeExecutionDialog()
        await this.loadTasks()
        this.updateStats()
      } catch (error) {
        this.showError('Error al completar picking')
        console.error('Error completing picking:', error)
      }
    },
    
    // Utilidades para items
    getItemStatusColor(status) {
      const colors = {
        'pending': 'warning',
        'picked': 'success',
        'cancelled': 'error'
      }
      return colors[status] || 'grey'
    },
    
    getItemStatusText(status) {
      const texts = {
        'pending': 'Pendiente',
        'picked': 'Recolectado',
        'cancelled': 'Cancelado'
      }
      return texts[status] || status
    },

    // === MÉTODOS DE CALLBACK PARA COMPONENTES ===
    
    onCriteriaChanged(line, index, event) {
      console.log(`🔄 Criteria changed for line ${index}:`, event)
      line.picking_criteria = { ...event }
      
      // Si es selección automática, recalcular criterios
      if (this.pickingStrategy.level === 'order' && this.pickingStrategy.strategy) {
        this.applyAutomaticStrategyForTask(line, this.pickingStrategy.strategy, index)
      }
    },
    
    onLineStrategyChanged(line, index, strategy) {
      console.log(`🎯 Line strategy changed for line ${index}:`, strategy)
      line.picking_strategy = strategy
      
      // Si es por producto, aplicar estrategia específica
      if (this.pickingStrategy.level === 'product' && strategy) {
        this.applyAutomaticStrategyForTask(line, strategy, index)
      }
    },
    
    validateLineForStrategy(line, lineIndex) {
      // Validaciones básicas
      line.can_select_criteria = line.has_any_criteria
      line.criteria_error = null
      
      if (!line.has_any_criteria) {
        line.criteria_error = 'Este producto no tiene criterios de trazabilidad disponibles'
      }
    },

    // === NUEVOS MÉTODOS PARA ESTRATEGIAS AUTOMÁTICAS Y SELECCIÓN MÚLTIPLE ===
    
    async applyAutomaticStrategyForTask(line, strategy, index) {
      if (!line.product_id || !line.has_any_criteria) return
      
      console.log(`🎯 Applying automatic strategy ${strategy} for task to line ${index}`)
      
      // Obtener los criterios disponibles ordenados según la estrategia
      const orderedCriteria = this.getOrderedCriteriaForStrategy(line, strategy)
      
      if (orderedCriteria.length === 0) return
      
      // Calcular qué criterios necesitamos para completar la cantidad
      const selectedCriteria = this.selectCriteriaToCompleteQuantity(line, orderedCriteria)
      
      // Asignar los criterios seleccionados automáticamente
      line.selected_criteria = selectedCriteria
      line.automatic_selection = true
      line.strategy_applied = strategy
      
      // Actualizar la cantidad máxima
      const totalAvailable = selectedCriteria.reduce((sum, criteria) => sum + criteria.available_quantity, 0)
      line.max_quantity = totalAvailable
      
      if (line.quantity > totalAvailable) {
        line.quantity = totalAvailable
      }
      
      console.log(`✅ Applied ${strategy} strategy - selected ${selectedCriteria.length} criteria for ${totalAvailable} units`)
    },
    
    getOrderedCriteriaForStrategy(line, strategy) {
      let criteria = []
      
      switch (strategy) {
        case 'fifo':
          // Ordenar por fecha de ingreso más antigua
          criteria = [...(line.available_lots || [])].sort((a, b) => 
            new Date(a.ingress_date || a.created_at || '1970-01-01') - 
            new Date(b.ingress_date || b.created_at || '1970-01-01')
          ).map(lot => ({
            type: 'lot',
            value: lot.value,
            display: `Lote: ${lot.value} (Ingreso: ${this.formatDate(lot.ingress_date || lot.created_at)})`,
            available_quantity: this.getAvailableQuantityForCriteria(line.product_id, 'lot', lot.value),
            date: lot.ingress_date || lot.created_at,
            sort_date: new Date(lot.ingress_date || lot.created_at || '1970-01-01')
          }))
          break
          
        case 'lifo':
          // Ordenar por fecha de ingreso más nueva
          criteria = [...(line.available_lots || [])].sort((a, b) => 
            new Date(b.ingress_date || b.created_at || '1970-01-01') - 
            new Date(a.ingress_date || a.created_at || '1970-01-01')
          ).map(lot => ({
            type: 'lot',
            value: lot.value,
            display: `Lote: ${lot.value} (Ingreso: ${this.formatDate(lot.ingress_date || lot.created_at)})`,
            available_quantity: this.getAvailableQuantityForCriteria(line.product_id, 'lot', lot.value),
            date: lot.ingress_date || lot.created_at,
            sort_date: new Date(lot.ingress_date || lot.created_at || '1970-01-01')
          }))
          break
          
        case 'fefo':
          // Ordenar por fecha de vencimiento más antigua
          criteria = [...(line.available_expiry_dates || [])].sort((a, b) => 
            new Date(a.value || '2099-12-31') - new Date(b.value || '2099-12-31')
          ).map(expiry => ({
            type: 'expiry',
            value: expiry.value,
            display: `Vence: ${this.formatDate(expiry.value)} (${expiry.available_quantity} unidades)`,
            available_quantity: this.getAvailableQuantityForCriteria(line.product_id, 'expiry', expiry.value),
            date: expiry.value,
            sort_date: new Date(expiry.value || '2099-12-31')
          }))
          break
          
        default:
          // Para estrategias manuales, no ordenar automáticamente
          break
      }
      
      return criteria
    },
    
    selectCriteriaToCompleteQuantity(line, orderedCriteria) {
      const requiredQuantity = line.quantity || 0
      const selectedCriteria = []
      let totalSelected = 0
      
      for (const criteria of orderedCriteria) {
        if (totalSelected >= requiredQuantity) break
        
        const availableInThisCriteria = Math.min(
          criteria.available_quantity,
          requiredQuantity - totalSelected
        )
        
        if (availableInThisCriteria > 0) {
          selectedCriteria.push({
            ...criteria,
            selected_quantity: availableInThisCriteria
          })
          totalSelected += availableInThisCriteria
          
          // Actualizar saldos usados
          this.updateUsedCriteriaSaldo(line.product_id, criteria.type, criteria.value, availableInThisCriteria)
        }
      }
      
      return selectedCriteria
    },
    
    getAvailableQuantityForCriteria(productId, criteriaType, criteriaValue) {
      const saldoKey = `${productId}_${criteriaType}_${criteriaValue}`
      const usedQuantity = this.usedCriteriaSaldos[saldoKey] || 0
      
      // Buscar la cantidad original del criterio
      const product = this.productsWithInventory.find(p => p.id === productId)
      if (!product) return 0
      
      // Buscar en los datos disponibles según el tipo
      let originalQuantity = 0
      if (criteriaType === 'lot') {
        // Necesitaríamos tener esta información del backend
        originalQuantity = 100 // Placeholder - esto debería venir del API
      } else if (criteriaType === 'expiry') {
        // Buscar en available_expiry_dates
        const expiryData = this.taskForm.task_lines.find(l => l.product_id === productId)
          ?.available_expiry_dates?.find(e => e.value === criteriaValue)
        originalQuantity = expiryData?.available_quantity || 0
      }
      
      return Math.max(0, originalQuantity - usedQuantity)
    },
    
    updateUsedCriteriaSaldo(productId, criteriaType, criteriaValue, usedQuantity) {
      const saldoKey = `${productId}_${criteriaType}_${criteriaValue}`
      this.usedCriteriaSaldos[saldoKey] = (this.usedCriteriaSaldos[saldoKey] || 0) + usedQuantity
    },
    
    clearLineCriteria(line) {
      line.picking_criteria = {
        type: null,
        value: null,
        available_quantity: 0
      }
      line.selected_criteria = []
      line.automatic_selection = false
      line.strategy_applied = null
      line.criteria_error = null
      
      // Restaurar cantidad máxima al stock total del producto
      if (line.product_id) {
        const product = this.productsWithInventory.find(p => p.id === line.product_id)
        if (product) {
          line.max_quantity = product.available_quantity
        }
      }
    },
    
         formatDate(dateString) {
       if (!dateString) return '-'
       try {
         return new Date(dateString).toLocaleDateString('es-ES', {
           year: 'numeric',
           month: '2-digit',
           day: '2-digit'
         })
       } catch {
         return dateString
       }
     }
  }
}
</script>

<style scoped>
.intelligent-picking {
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  min-height: 100vh;
}

.stats-card {
  transition: transform 0.2s;
}

.stats-card:hover {
  transform: translateY(-2px);
}

.tasks-table {
  background: white;
}

.success.lighten-4 {
  background-color: #c8e6c9 !important;
}

/* Responsive Design */
@media (max-width: 768px) {
  .intelligent-picking {
    padding: 15px;
  }
  
  /* Header responsivo */
  .picking-header {
    flex-direction: column;
    align-items: stretch !important;
    gap: 15px;
  }
  
  .picking-header h1 {
    font-size: 1.5rem !important;
    text-align: center;
  }
  
  .picking-create-btn {
    width: 100%;
    justify-content: center;
  }
  
  /* Filtros responsivos */
  .picking-filters .v-card-text {
    padding: 12px !important;
  }
  
  /* Stats cards en móvil */
  .stats-card .v-card-text {
    padding: 12px !important;
  }
  
  .stats-card .text-h4 {
    font-size: 1.5rem !important;
  }
  
  .stats-card .text-subtitle-2 {
    font-size: 0.75rem !important;
  }
}

@media (max-width: 480px) {
  .intelligent-picking {
    padding: 10px;
  }
  
  /* Header extra compacto */
  .picking-header h1 {
    font-size: 1.25rem !important;
  }
  
  /* Stats cards más compactos */
  .stats-card .v-card-text {
    padding: 8px !important;
  }
  
  .stats-card .v-icon {
    margin-right: 8px !important;
  }
  
  .stats-card .text-h4 {
    font-size: 1.25rem !important;
  }
  
  /* Filtros más compactos */
  .picking-filters .v-card-text {
    padding: 8px !important;
  }
}

/* Estilos para criterios seleccionados */
.selected-criteria-list {
  max-height: 120px;
  overflow-y: auto;
}

.selected-criteria-list .v-chip {
  margin-bottom: 4px !important;
}

/* Mejoras visuales para criterios */
.success.lighten-4 {
  background-color: #c8e6c9 !important;
}

.v-card[color="success"] {
  border-color: #4caf50 !important;
}

.text-success {
  color: #4caf50 !important;
}
</style> 